import React, { useState } from 'react';
import { Clock, X, CreditCard } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { formatDate } from '../utils/formatDate';
import { logPublicEvent } from '../lib/publicEvent';
import PlanChoiceCard from './PlanChoiceCard';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export default function SubscriptionBanner() {
  const { user, hasValidSubscription, trialDaysRemaining, fetchWithAuth } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!user) return null;

  // NOTE: These handlers MUST be declared before the early returns below.
  // The `!user.subscription` branch (and others) reference handleUpgrade in
  // their onClick — if the handler is defined further down, that branch returns
  // before the `const` initializes, and clicking throws a TDZ ReferenceError
  // (button silently does nothing). This was breaking the primary "Start Free
  // Trial" CTA for every newly-registered, unpaid user.
  const handleUpgrade = async (plan = null, maximizer = false) => {
    setLoading(true);
    try {
      const selectedPlan = plan || localStorage.getItem('rigacap_selected_plan') || 'monthly';
      logPublicEvent('checkout_redirect', { path: '/app', utm_source: 'banner', utm_campaign: selectedPlan + (maximizer ? '_both' : '') });
      const response = await fetchWithAuth(`${API_URL}/api/billing/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: selectedPlan, maximizer: !!maximizer }),
      });
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      } else {
        const error = await response.json().catch(() => ({}));
        alert(error.detail || 'Failed to create checkout session');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      alert('Failed to start checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setLoading(true);
    try {
      const response = await fetchWithAuth(`${API_URL}/api/billing/portal`, { method: 'POST' });
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.portal_url;
      } else {
        const error = await response.json().catch(() => ({}));
        alert(error.detail || 'Failed to open billing portal');
      }
    } catch (err) {
      console.error('Portal error:', err);
      alert('Failed to open billing portal. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Free-first (project_free_first_spec §7): a non-valid authenticated user is on the free/proof
  // tier — no sub yet, or a trial whose full-product window phased out, or expired/canceled. Show a
  // PERSISTENT "unlock the full product" CTA (Erik: always there for the free tier). past_due falls
  // through to its own payment banner; active/valid-trial users have full access → no banner.
  if (!hasValidSubscription && user.subscription?.status !== 'past_due') {
    return (
      <div className="border border-claret/30 rounded p-5 mb-6 bg-claret/5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <CreditCard className="text-claret flex-shrink-0" size={20} />
            <div>
              <p className="font-medium text-ink">Unlock the full product</p>
              <p className="text-sm text-ink-mute mt-1">
                You&rsquo;re on the free view &mdash; subscribe to see today&rsquo;s live signals, book weights, and sell alerts. Both books (what your trial had) $138/mo, or Preserver alone $59/mo &middot; intro rates locked 12 months &middot; 30-day money-back.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              onClick={() => handleUpgrade('monthly', false)}
              disabled={loading}
              className="px-4 py-2.5 border border-rule-dark text-ink font-medium rounded-[2px] hover:border-ink transition-colors disabled:opacity-50"
            >
              Preserver &middot; $59
            </button>
            <button
              onClick={() => handleUpgrade('monthly', true)}
              disabled={loading}
              className="px-4 py-2.5 bg-ink text-paper font-medium rounded-[2px] hover:bg-claret transition-colors disabled:opacity-50"
            >
              {loading ? 'Loading…' : 'Both books · $138'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // IN-TRIAL ask (Oct 2026): a no-card trial has FULL access for 14 days from trial_start (the
  // server's days_remaining counts to the 30-day trial_end, so the old countdown could never fire).
  // From day 10 show a slim, honest heads-up with a one-click upgrade.
  const _sub = user.subscription || {};
  if (hasValidSubscription && _sub.status === 'trial' && !_sub.has_stripe_subscription && _sub.trial_start) {
    const fullEnd = new Date(new Date(_sub.trial_start).getTime() + 14 * 86400000);
    const daysLeft = Math.ceil((fullEnd - new Date()) / 86400000);
    if (daysLeft >= 0 && daysLeft <= 4 && !dismissed) {
      const when = daysLeft <= 0 ? 'today' : daysLeft === 1 ? 'tomorrow' : `in ${daysLeft} days`;
      return (
        <div className="border border-claret/30 rounded p-4 mb-6 bg-claret/5">
          <div className="flex items-start justify-between gap-3 mb-3">
            <p className="text-sm text-ink">
              <span className="font-medium">Your full access ends {when}.</span>{' '}
              <span className="text-ink-mute">You&rsquo;ve had both books. Keep them, or step down to Preserver &mdash; your call.</span>
            </p>
            <button onClick={() => setDismissed(true)} className="p-1 text-ink-light hover:text-ink flex-shrink-0" aria-label="Dismiss"><X size={16} /></button>
          </div>
          <PlanChoiceCard mode="trial" loading={loading} compact onChoose={(plan, mx) => handleUpgrade(plan, mx)} />
        </div>
      );
    }
  }

  const subStatus = user.subscription?.status;
  const isActive = subStatus === 'active';
  const isPastDue = subStatus === 'past_due';
  const isTrialExpired = subStatus === 'trial' && trialDaysRemaining === 0;
  const isTrialExpiring = subStatus === 'trial' && trialDaysRemaining <= 3 && trialDaysRemaining > 0;

  const cancelEndDate = user.subscription?.current_period_end ? new Date(user.subscription.current_period_end) : null;
  const daysUntilCancel = cancelEndDate ? Math.ceil((cancelEndDate - new Date()) / (1000 * 60 * 60 * 24)) : null;
  const isCanceling = isActive && user.subscription?.cancel_at_period_end && daysUntilCancel !== null && daysUntilCancel <= 30;

  if (dismissed || (isActive && !isCanceling)) return null;
  if (!isTrialExpired && !isTrialExpiring && !isPastDue && !isCanceling) return null;

  const getMessage = () => {
    if (isCanceling) {
      const endDate = user.subscription?.current_period_end;
      const formatted = endDate ? formatDate(endDate, { includeYear: true }) : 'soon';
      return `Your subscription is set to cancel on ${formatted}.`;
    }
    if (isPastDue) return 'Your payment failed. Please update your payment method to continue.';
    if (isTrialExpired) return 'Your free trial has ended. Subscribe to continue using RigaCap.';
    if (trialDaysRemaining === 1) return 'Your free trial ends tomorrow.';
    return `Your free trial ends in ${trialDaysRemaining} days.`;
  };

  const isUrgent = isTrialExpired || isPastDue;

  if (isPastDue) {
    return (
      <div className="border border-negative/30 rounded p-5 mb-6 bg-negative/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CreditCard className="text-negative flex-shrink-0" size={20} />
            <p className="font-medium text-ink">{getMessage()}</p>
          </div>
          <button
            onClick={handleManageSubscription}
            disabled={loading}
            className="px-5 py-2.5 bg-negative text-paper font-medium rounded-[2px] hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
          >
            <CreditCard size={16} />
            {loading ? 'Loading...' : 'Update Payment'}
          </button>
        </div>
      </div>
    );
  }

  if (isCanceling) {
    return (
      <div className="border border-rule-dark rounded p-5 mb-6 bg-paper-deep">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Clock className="text-ink-mute flex-shrink-0" size={20} />
            <p className="font-medium text-ink">{getMessage()}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleManageSubscription}
              disabled={loading}
              className="px-5 py-2.5 bg-ink text-paper font-medium rounded-[2px] hover:bg-claret transition-colors disabled:opacity-50"
            >
              {loading ? 'Loading...' : 'Resubscribe'}
            </button>
            <button onClick={() => setDismissed(true)} className="p-2 text-ink-light hover:text-ink">
              <X size={18} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`border rounded p-5 mb-6 ${isUrgent ? 'border-claret/30 bg-claret/5' : 'border-rule-dark bg-paper-card'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Clock className={`flex-shrink-0 ${isUrgent ? 'text-claret' : 'text-ink-mute'}`} size={20} />
          <div>
            <p className="font-medium text-ink">{getMessage()}</p>
            <p className="text-sm text-ink-mute mt-1">
              $129/month or $1,099/year (three months free)
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => handleUpgrade('monthly')}
            disabled={loading}
            className="px-4 py-2.5 border border-rule-dark text-ink font-medium rounded-[2px] hover:border-ink transition-colors disabled:opacity-50"
          >
            Monthly
          </button>
          <button
            onClick={() => handleUpgrade('annual')}
            disabled={loading}
            className="px-4 py-2.5 bg-ink text-paper font-medium rounded-[2px] hover:bg-claret transition-colors disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Annual (Save $449)'}
          </button>
          {!isTrialExpired && (
            <button onClick={() => setDismissed(true)} className="p-2 text-ink-light hover:text-ink">
              <X size={18} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
